import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  MessageBarBody,
  MessageBarTitle,
  Spinner
} from '@fluentui/react-components';
import { Add20Regular, Checkmark20Regular, Dismiss20Regular, Edit20Regular, Save20Regular, Search20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

// guía
import { StatusPill } from '../../../theme/components/StatusPill';
import { GuideRowActions } from '../../../theme/components/GuideTable';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideTooltip } from '../../../theme/components/GuideTooltip';

// lógica
import { PRICE_LIST_NAME_MAX, priceListStatus, validatePriceListName } from '../PriceList.Logic';

import * as ComponentFunctions from './PriceListHeader.Functions';
import './PriceListHeader.css';

/**
 * Encabezado compacto de la lista elegida (guía): en una sola línea el nombre (editable en
 * línea) con su etiqueta de estado, la búsqueda de servicios y, a la derecha, la edición masiva,
 * "Agregar precio" y un menú ⋯ con Duplicar y Eliminar (con confirmación). La vigencia va en su
 * propio panel debajo.
 */
const PriceListHeaderComponentBase = (props: ComponentFunctions.Props) => {
  const [t] = useTranslation();

  const listName = props.PriceList.priceList?.listName ?? '';

  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [massiveActive, setMassiveActive] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [search, setSearch] = useState('');

  // Al cambiar de lista se descarta el nombre a medio escribir, el filtro y la edición masiva.
  useEffect(() => {
    setEditingName(false);
    setDraftName('');
    setNameTouched(false);
    setSearch('');
    if (massiveActive) {
      setMassiveActive(false);
      props.onMassiveUpdateChange(false);
    }
  }, [props.PriceList.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Si el borrado terminó bien el padre desmonta este encabezado; si falló, el diálogo sigue
  // abierto mostrando el error.
  useEffect(() => {
    if (!props.isDeletingExecution && !props.FailOnDeleting) setConfirmingDelete(false);
  }, [props.isDeletingExecution, props.FailOnDeleting]);

  const nameError = nameTouched ? validatePriceListName(draftName) : undefined;

  const startRename = () => {
    setDraftName(listName);
    setNameTouched(false);
    setEditingName(true);
  };

  const confirmRename = () => {
    setNameTouched(true);
    if (validatePriceListName(draftName)) return;
    const trimmed = draftName.trim();
    if (trimmed !== listName) props.UpdatePriceList({ ...props.PriceList.priceList, listName: trimmed });
    setEditingName(false);
  };

  const cancelRename = () => {
    setEditingName(false);
    setDraftName('');
    setNameTouched(false);
  };

  const toggleMassive = () => {
    const next = !massiveActive;
    setMassiveActive(next);
    props.onMassiveUpdateChange(next);
  };

  const onSearch = (value: string) => {
    setSearch(value);
    props.onSearchService?.(value.trim() || undefined);
  };

  const cancelDelete = () => {
    setConfirmingDelete(false);
    props.onCancelDeletePriceList();
  };

  const status = priceListStatus(props.PriceList);

  return (
    <div className="plh" data-testid="price-list-header">
      <div className="plh__row">
        {!editingName ? (
          <div className="plh__title-row">
            <h3 className="plh__title" title={listName}>{listName}</h3>
            <GuideTooltip content={t('priceListHeader.buttons.edit')} relationship="label">
              <Button appearance="subtle" size="small" icon={<Edit20Regular />} onClick={startRename} data-testid="plh-rename" />
            </GuideTooltip>
            <StatusPill tone={status.tone} symbol={status.symbol} size="small">{t(status.i18nKey)}</StatusPill>
          </div>
        ) : (
          <form
            className="plh__rename"
            onSubmit={(event) => { event.preventDefault(); confirmRename(); }}
            data-testid="plh-rename-form"
          >
            <Input
              className="plh__rename-input"
              size="small"
              value={draftName}
              autoFocus
              maxLength={PRICE_LIST_NAME_MAX}
              aria-label={t('priceListHeader.renameLabel')}
              aria-invalid={!!nameError}
              placeholder={t('priceListHeader.renamePlaceholder')}
              onChange={(_event, data) => setDraftName(data.value)}
              onKeyDown={(event) => { if (event.key === 'Escape') cancelRename(); }}
            />
            <GuideTooltip content={t('priceListHeader.buttons.confirm')} relationship="label">
              <Button appearance="primary" size="small" type="submit" icon={<Checkmark20Regular />} />
            </GuideTooltip>
            <GuideTooltip content={t('priceListHeader.buttons.cancel')} relationship="label">
              <Button appearance="subtle" size="small" type="button" icon={<Dismiss20Regular />} onClick={cancelRename} />
            </GuideTooltip>
            {nameError && (
              <span className="plh__rename-error" role="alert">
                {t(nameError === 'required' ? 'priceListHeader.nameRequired' : 'priceListHeader.nameTooLong', { max: PRICE_LIST_NAME_MAX })}
              </span>
            )}
          </form>
        )}

        <Input
          className="plh__search"
          size="small"
          type="search"
          value={search}
          placeholder={t('priceListHeader.buttons.searchService')}
          aria-label={t('priceListHeader.buttons.searchService')}
          contentBefore={<Search20Regular />}
          maxLength={200}
          onChange={(_event, data) => onSearch(data.value)}
          data-testid="plh-search"
        />

        <div className="plh__actions">
          <Button
            size="small"
            appearance={massiveActive ? 'primary' : 'secondary'}
            icon={massiveActive ? <Save20Regular /> : <Edit20Regular />}
            onClick={toggleMassive}
            aria-pressed={massiveActive}
            data-testid="plh-massive"
          >
            {massiveActive ? t('priceListHeader.buttons.saveMassive') : t('priceListHeader.buttons.modifyPricesMassively')}
          </Button>
          <Button size="small" appearance="primary" icon={<Add20Regular />} onClick={() => props.onAddMedicalService?.()} disabled={massiveActive} data-testid="plh-add">
            {t('priceListHeader.buttons.addPrice')}
          </Button>
          <GuideRowActions
            label={t('priceListHeader.moreActions', { name: listName })}
            actions={[
              { key: 'clone', label: t('priceListHeader.buttons.clonePrice'), onClick: () => props.onClonePriceList(props.PriceList.id) },
              { key: 'delete', label: t('priceListHeader.buttons.deleteButton'), danger: true, onClick: () => setConfirmingDelete(true) }
            ]}
          />
        </div>
      </div>

      <Dialog open={confirmingDelete} modalType="alert" onOpenChange={(_event, data) => { if (!data.open && !props.isDeletingExecution) cancelDelete(); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('priceListHeader.buttons.deletePriceListInformation')}</DialogTitle>
            <DialogContent>
              <p className="plh__dialog-text">{t('priceListHeader.buttons.descriptionDeletePriceList')}</p>
              <p className="plh__dialog-name">
                <span className="plh__dialog-label">{t('priceListHeader.buttons.deletePriceList')}</span> {listName}
              </p>
              {props.FailOnDeleting && (
                <GuideMessageBar intent="error" layout="multiline" data-testid="plh-delete-error">
                  <MessageBarBody>
                    <MessageBarTitle>{t('priceListHeader.deleteFailed')}</MessageBarTitle>
                    {props.ErrorOnDeleting?.ErrorMessage}
                  </MessageBarBody>
                </GuideMessageBar>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={cancelDelete} disabled={props.isDeletingExecution}>
                {t('priceListHeader.buttons.cancelButtonText')}
              </Button>
              <Button
                appearance="primary"
                className="plh__danger"
                icon={props.isDeletingExecution ? <Spinner size="extra-tiny" /> : undefined}
                disabled={props.isDeletingExecution}
                onClick={() => props.onDeletePriceList(props.PriceList.id)}
                data-testid="plh-delete-confirm"
              >
                {t('priceListHeader.buttons.deleteButtonText')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export const PriceListHeaderComponent = connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(PriceListHeaderComponentBase as any) as any;

export default PriceListHeaderComponent;
